import { type MaybeRefOrGetter, ref, toRef, toValue, watch } from 'vue';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { allocationsService } from '@/api/allocations.service.ts';
import {
  type AllocationsControllerFindAllData,
  type AllocationsControllerFindAllMinimalData,
  type AllocationsControllerGetAllocationTransactionsData,
  AllocationType,
  type CreateAllocationDto,
  type UpdateAllocationDto,
} from '@carvia/ros-client-types';
import {
  addItemToQueriesData,
  removeItemFromQueriesData,
  updateItemOnQueriesData,
} from '@/utils/update-query-data.ts';
import type { Allocation } from '@/entities/allocations/allocation.entity.ts';
import { useRouter } from 'vue-router';

export const ALLOCATIONS_QUERY_KEY = 'allocations';
const QUERY_KEY = ALLOCATIONS_QUERY_KEY;

export const useAllocation = (
  id: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, toRef(id)],
    queryFn: async () => allocationsService.getOne(toValue(id)),
    enabled: toRef(enabled),
  });
};

export const useAllocationOfTypeOrReroute = (
  id: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
  requiredTypes: AllocationType | AllocationType[],
  rerouteName: string,
) => {
  const { data: allocation, ...rest } = useAllocation(id, enabled);
  const router = useRouter();
  const responseValue = ref<Allocation>();
  const requiredTypesArray = Array.isArray(requiredTypes) ? requiredTypes : [requiredTypes];
  watch(
    allocation,
    () => {
      if (allocation.value && !requiredTypesArray.includes(allocation.value.type)) {
        responseValue.value = undefined;
        rerouteName && router.push({ name: rerouteName });
      } else if (allocation.value) {
        responseValue.value = allocation.value;
      }
    },
    { immediate: true },
  );
  return { data: responseValue, ...rest };
};

export const useAllocations = (
  query: MaybeRefOrGetter<AllocationsControllerFindAllData['query']>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY, toRef(query)],
    queryFn: ({ pageParam = 1 }) =>
      allocationsService.getAllWithQuery({ ...toValue(query), page: pageParam }),
    enabled: toRef(enabled),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
  });
};

export const useMinimalAllocations = (
  query: MaybeRefOrGetter<AllocationsControllerFindAllMinimalData['query']>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'minimal', toRef(query)],
    queryFn: () => allocationsService.getAllMinimal(toValue(query)),
    enabled: toRef(enabled),
  });
};

export const useAllocationTransactions = (
  query: MaybeRefOrGetter<AllocationsControllerGetAllocationTransactionsData['query']>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY, toRef(query)],
    queryFn: ({ pageParam = 1 }) =>
      allocationsService.getAllocationTransactions({ ...toValue(query), page: pageParam }),
    enabled: toRef(enabled),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
  });
};

export const useCreateAllocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: (allocation: CreateAllocationDto) => allocationsService.create(allocation),
    onMutate: () => ({
      successMessage: 'Allocation created successfully',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      addItemToQueriesData<Allocation>([QUERY_KEY], result, queryClient);
    },
  });
};

export const useUpdateAllocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: ({ id, allocation }: { id: string; allocation: UpdateAllocationDto }) =>
      allocationsService.update(id, allocation),
    onMutate: () => ({
      successMessage: 'Allocation updated',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      updateItemOnQueriesData<Allocation>([QUERY_KEY], result, queryClient);
    },
  });
};

export const useDeleteAllocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: (removeId: string) => allocationsService.remove(removeId),
    onMutate: () => ({
      successMessage: 'Allocation deleted successfully',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      removeItemFromQueriesData<Allocation>([QUERY_KEY], variables, queryClient);
    },
  });
};
